import {
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	computed,
	inject,
} from '@angular/core'
import { TranslationService } from '../../services/translation.service.js'
import { Subscription, map, scan, shareReplay, skip } from 'rxjs'
import { AppContext } from '../../app-context.js'
import { FrontendLanguageBridge } from '../../bridges/frontend-language-bridge.js'
import { BuildStatus } from '@lg/shared'
import { DevBarService } from '../../services/dev-bar.service.js'
import { LayoutService } from '../../services/layout-service.js'
import { AppTrpcServiceInjectionToken } from '../../services/app-trpc.service.js'
import { BoReference } from '@lg/shared'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormCanDeactivate } from '../../directives/form-can-deactivate.js'

@Component({
	selector: 'dev-bar',
	templateUrl: './dev-bar.component.html',
	styleUrls: ['./dev-bar.component.scss'],
	// changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DevBarComponent implements OnInit, OnDestroy {
	private trpc = inject(AppTrpcServiceInjectionToken)
	subscriptions: Subscription[] = []
	languages = AppContext.languages
	languageBridge = FrontendLanguageBridge
	buildStatus$ = this.trpc
		.subscriptionAsObservable((c) => c.build.getStatus$, {
			branchName: AppContext.branchName,
			appModuleId: AppContext.appModuleId,
		})
		.pipe(shareReplay(1))
	buildStatusColor$ = this.buildStatus$.pipe(
		scan(
			(prev, buildStatus) => {
				if (prev.buildStatus && buildStatus.status == 'Running') {
					return { dirty: true, buildStatus }
				}
				return { dirty: false, buildStatus }
			},
			{ dirty: false, buildStatus: null } as {
				dirty: boolean
				buildStatus: BuildStatus | null
			},
		),
		map(({ dirty, buildStatus }) =>
			dirty ? 'greenyellow' : buildStatus?.bgColor,
		),
		shareReplay(1),
	)
	buildStatusColor = toSignal(this.buildStatusColor$)
	isBuilding = toSignal(
		this.buildStatus$.pipe(map((s) => s?.status === 'Building')),
		{ initialValue: false },
	)
	borderBottomStyle = computed(() => {
		const envSettings = this.devBarService.environmentSettings()
		return envSettings ? '0.5em solid ' + envSettings.bgColor : ''
	})

	isHandset = true

	constructor(
		public devBarService: DevBarService,
		public translationService: TranslationService,
		private cdRef: ChangeDetectorRef,
		private layoutService: LayoutService,
	) {
		this.buildStatus$.pipe(skip(1)).subscribe((buildStatus) => {
			if (
				buildStatus.status == 'Running' &&
				this.devBarService.isAutoReload()
			) {
				this.reloadApp(this.devBarService.isKeepData())
			}
		})
	}

	async ngOnInit() {
		await this.devBarService.init()
		if (!this.devBarService.canUseDevBar) return

		this.layoutService.breakpoints$.subscribe(
			(breakpoints) => (this.isHandset = breakpoints.isHandset),
		)
		this.cdRef.detectChanges()
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => sub.unsubscribe())
	}

	reloadApp(keepData: boolean) {
		FormCanDeactivate.unregisterAllForms()
		if (keepData) {
			this.devBarService.saveToSessionStorage()
		}
		window.location.reload()
	}

	openMainScreenEditor() {
		const boRef = BoReference.fromQualifiedName(
			this.devBarService.mainScreenName()!,
			'Screen',
		)
		this.devBarService.openBoEditor(AppContext.branchName, boRef)
	}

	openMainProcessEditor() {
		const boRef = BoReference.fromQualifiedName(
			this.devBarService.getTopLevelProcessName(),
			'Process',
		)
		this.devBarService.openBoEditor(AppContext.branchName, boRef)
	}

	onMouseLeave() {
		if (!this.devBarService.isFloating()) {
			this.devBarService.isCollapsed.set(true)
		}
	}

	toggleTransparent() {
		this.devBarService.setIsTransparent(!this.devBarService.isTransparent())
	}

	togglePin() {
		const pinned = !this.devBarService.isFloating()
		this.devBarService.setIsFloating(pinned)
		if (!pinned) {
			this.devBarService.isCollapsed.set(true)
		}
	}
}
